import { PERMISSIONS } from "./permissions.js";
export const CUSTOMER_360_CONTRACT_VERSION = "1.0";
export const CUSTOMER_360_VISIBILITY_LEVELS = [
    "FULL",
    "SUMMARY",
    "TASK_ONLY",
    "MASKED",
    "RESTRICTED",
    "HIDDEN",
];
export const CUSTOMER_360_EDITABILITY_LEVELS = ["EDIT", "READ_ONLY", "NOT_AVAILABLE"];
export const CUSTOMER_360_SOURCE_LABELS = [
    "VERIFIED_CUSTOMER_DATA",
    "STAFF_ENTERED",
    "ORDER_DERIVED",
    "INQUIRY_DERIVED",
    "WHATSAPP_DERIVED",
    "PAYMENT_DERIVED",
    "SYSTEM_INFERRED",
    "LEGACY_PROFILE",
    "UNAVAILABLE",
];
export const CUSTOMER_360_ACTIVITY_CATEGORIES = [
    "CUSTOMER",
    "SALES",
    "FOLLOW_UP",
    "ORDER",
    "FINANCE",
    "WHATSAPP",
    "MEMORY",
    "AUDIT",
    "SYSTEM",
];
export const CUSTOMER_360_ACTIVITY_TYPES = [
    "CUSTOMER_CREATED",
    "CUSTOMER_UPDATED",
    "ADDRESS_CREATED",
    "OCCASION_RECORDED",
    "PREFERENCE_RECORDED",
    "NOTE_ADDED",
    "NOTE_PINNED",
    "NOTE_ARCHIVED",
    "RELATIONSHIP_ASSIGNED",
    "RELATIONSHIP_UNASSIGNED",
    "INQUIRY_CREATED",
    "INQUIRY_CONVERSATION_UPDATED",
    "INQUIRY_LOST",
    "INQUIRY_CONVERTED",
    "FOLLOW_UP_CREATED",
    "FOLLOW_UP_COMPLETED",
    "ORDER_CREATED",
    "ORDER_STATUS_CHANGED",
    "ORDER_CANCELLED",
    "INVOICE_CREATED",
    "PAYMENT_RECORDED",
    "WHATSAPP_CONVERSATION_OPENED",
    "WHATSAPP_INBOUND_RECEIVED",
    "WHATSAPP_HUMAN_REPLY_SENT",
    "WHATSAPP_RM_ASSIGNED",
    "CUSTOMER_EVENT_RECORDED",
    "AUDIT_RECORDED",
];
export const CUSTOMER_360_SUMMARY_CARD_TYPES = [
    "OPEN_INQUIRY",
    "NEXT_OCCASION",
    "OUTSTANDING_AMOUNT",
    "LAST_ORDER",
];
export const CUSTOMER_360_ALERT_TYPES = [
    "UPCOMING_OCCASION",
    "PAYMENT_OVERDUE",
    "NO_FOLLOW_UP",
    "CUSTOMER_DORMANT",
    "ALLERGY_WARNING",
    "DELIVERY_INSTRUCTION",
    "OPEN_OPERATIONAL_ISSUE",
];
export const CUSTOMER_360_QUICK_ACTIONS = [
    "CALL",
    "OPEN_WHATSAPP",
    "CREATE_INQUIRY",
    "CREATE_ORDER",
    "SCHEDULE_FOLLOW_UP",
    "ADD_NOTE",
    "RECORD_OCCASION",
    "ADD_PREFERENCE",
    "ADD_ADDRESS",
    "VIEW_OUTSTANDING",
    "EDIT_CUSTOMER",
    "ASSIGN_RM",
];
export const CUSTOMER_360_SECTION_IDS = [
    "customer",
    "relationship",
    "operationalState",
    "alerts",
    "summaryCards",
    "preferences",
    "memory",
    "communication",
    "metrics",
    "dataCompleteness",
    "quickActions",
    "metadata",
];
export const CUSTOMER_360_PRIMARY_TYPE_PRECEDENCE = ["DORMANT", "AT_RISK", "VIP", "REPEAT", "NEW"];
export const CUSTOMER_360_PERMISSION_REQUIREMENTS = {
    customer: [PERMISSIONS.CUSTOMER_READ],
    relationship: [PERMISSIONS.CUSTOMER_READ],
    operationalState: [PERMISSIONS.CUSTOMER_READ],
    alerts: [PERMISSIONS.CUSTOMER_READ],
    summaryCards: [PERMISSIONS.CUSTOMER_READ],
    preferences: [PERMISSIONS.CUSTOMER_READ],
    memory: [PERMISSIONS.INTELLIGENCE_CUSTOMER_READ],
    communication: [PERMISSIONS.WHATSAPP_READ],
    metrics: [PERMISSIONS.CUSTOMER_READ],
    dataCompleteness: [PERMISSIONS.CUSTOMER_READ],
    quickActions: [PERMISSIONS.CUSTOMER_READ],
    metadata: [PERMISSIONS.CUSTOMER_READ],
};
export const CUSTOMER_360_QUICK_ACTION_REQUIRED_PERMISSIONS = {
    CALL: PERMISSIONS.ORDER_READ,
    OPEN_WHATSAPP: PERMISSIONS.WHATSAPP_READ,
    CREATE_INQUIRY: PERMISSIONS.LEAD_CREATE,
    CREATE_ORDER: PERMISSIONS.ORDER_CREATE,
    SCHEDULE_FOLLOW_UP: PERMISSIONS.FOLLOW_UP_MANAGE,
    ADD_NOTE: PERMISSIONS.CUSTOMER_NOTE_CREATE,
    RECORD_OCCASION: PERMISSIONS.CUSTOMER_OCCASION_MANAGE,
    ADD_PREFERENCE: PERMISSIONS.CUSTOMER_PREFERENCE_MANAGE,
    ADD_ADDRESS: PERMISSIONS.CUSTOMER_ADDRESS_MANAGE,
    VIEW_OUTSTANDING: PERMISSIONS.INVOICE_READ,
    EDIT_CUSTOMER: PERMISSIONS.CUSTOMER_UPDATE,
    ASSIGN_RM: PERMISSIONS.CUSTOMER_RELATIONSHIP_ASSIGN,
};
export const CUSTOMER_360_DERIVED_METRIC_RULES = {
    totalOrders: {
        sourceModel: "Order",
        derivationRule: "Count non-deleted orders linked to customer.",
        source: "ORDER_DERIVED",
        cached: false,
    },
    deliveredOrders: {
        sourceModel: "Order",
        derivationRule: "Count non-deleted orders whose status is DELIVERED or COLLECTED.",
        source: "ORDER_DERIVED",
        cached: false,
    },
    cancelledOrders: {
        sourceModel: "Order",
        derivationRule: "Count non-deleted orders whose status is CANCELLED.",
        source: "ORDER_DERIVED",
        cached: false,
    },
    lostInquiries: {
        sourceModel: "Inquiry",
        derivationRule: "Count customer-linked inquiries whose status is LOST.",
        source: "INQUIRY_DERIVED",
        cached: false,
    },
    totalSpendPaise: {
        sourceModel: "Order",
        derivationRule: "Sum paise totals from completed revenue-bearing orders. Never use floating-point money.",
        source: "ORDER_DERIVED",
        cached: false,
    },
    averageOrderValuePaise: {
        sourceModel: "CustomerProfile",
        derivationRule: "Use CustomerProfile average order value when available; otherwise divide total revenue paise by delivered/collected order count.",
        source: "ORDER_DERIVED",
        cached: true,
    },
    outstandingPaise: {
        sourceModel: "Invoice",
        derivationRule: "Sum unpaid invoice/order balances in paise for customer-linked active orders.",
        source: "PAYMENT_DERIVED",
        cached: false,
    },
    lastOrderAt: {
        sourceModel: "Order",
        derivationRule: "Most recent order created/delivery timestamp from non-deleted customer-linked orders.",
        source: "ORDER_DERIVED",
        cached: false,
    },
    lastInquiryAt: {
        sourceModel: "Inquiry",
        derivationRule: "Most recent inquiry createdAt or lastConversationAt for customer-linked inquiries.",
        source: "INQUIRY_DERIVED",
        cached: false,
    },
    lastContactAt: {
        sourceModel: "System",
        derivationRule: "Maximum of inquiry conversation date, WhatsApp customer message date, and last human reply date.",
        source: "SYSTEM_INFERRED",
        cached: false,
    },
};
const ALL_SECTIONS_VISIBLE = Object.fromEntries(CUSTOMER_360_SECTION_IDS.map((section) => [section, "FULL"]));
const READ_ONLY_SECTIONS = Object.fromEntries(CUSTOMER_360_SECTION_IDS.map((section) => [section, "READ_ONLY"]));
function rolePolicy(role, fields, overrides, quickActions) {
    return {
        role,
        sections: { ...ALL_SECTIONS_VISIBLE, ...overrides },
        fields,
        editable: READ_ONLY_SECTIONS,
        quickActions,
    };
}
const ADMIN_ACTIONS = [
    "CALL",
    "OPEN_WHATSAPP",
    "CREATE_INQUIRY",
    "CREATE_ORDER",
    "SCHEDULE_FOLLOW_UP",
    "ADD_NOTE",
    "RECORD_OCCASION",
    "ADD_PREFERENCE",
    "ADD_ADDRESS",
    "EDIT_CUSTOMER",
    "ASSIGN_RM",
    "VIEW_OUTSTANDING",
];
const SALES_ACTIONS = [
    "CALL",
    "OPEN_WHATSAPP",
    "CREATE_INQUIRY",
    "CREATE_ORDER",
    "SCHEDULE_FOLLOW_UP",
    "ADD_NOTE",
    "RECORD_OCCASION",
    "ADD_PREFERENCE",
    "ADD_ADDRESS",
];
export const CUSTOMER_360_ROLE_VISIBILITY = {
    OWNER: rolePolicy("OWNER", {
        phone: "FULL",
        email: "FULL",
        finance: "FULL",
        communicationPreview: "FULL",
        memory: "FULL",
        preferences: "FULL",
        privateNotes: "FULL",
        allergyDetails: "FULL",
        auditMetadata: "FULL",
    }, {}, ADMIN_ACTIONS),
    MASTER_ADMIN: rolePolicy("MASTER_ADMIN", {
        phone: "FULL",
        email: "FULL",
        finance: "FULL",
        communicationPreview: "FULL",
        memory: "FULL",
        preferences: "FULL",
        privateNotes: "FULL",
        allergyDetails: "FULL",
        auditMetadata: "FULL",
    }, {}, ADMIN_ACTIONS),
    SUPER_ADMIN: rolePolicy("SUPER_ADMIN", {
        phone: "FULL",
        email: "FULL",
        finance: "FULL",
        communicationPreview: "FULL",
        memory: "FULL",
        preferences: "FULL",
        privateNotes: "FULL",
        allergyDetails: "FULL",
        auditMetadata: "FULL",
    }, {}, ADMIN_ACTIONS),
    OPERATIONS_MANAGER: rolePolicy("OPERATIONS_MANAGER", {
        phone: "FULL",
        email: "FULL",
        finance: "SUMMARY",
        communicationPreview: "SUMMARY",
        memory: "SUMMARY",
        preferences: "FULL",
        privateNotes: "SUMMARY",
        allergyDetails: "FULL",
        auditMetadata: "RESTRICTED",
    }, { metrics: "SUMMARY", communication: "SUMMARY" }, ["CALL", "OPEN_WHATSAPP", "CREATE_INQUIRY", "CREATE_ORDER", "SCHEDULE_FOLLOW_UP", "ADD_NOTE", "RECORD_OCCASION", "ADD_PREFERENCE", "ADD_ADDRESS", "EDIT_CUSTOMER", "ASSIGN_RM"]),
    SALES_MANAGER: rolePolicy("SALES_MANAGER", {
        phone: "FULL",
        email: "FULL",
        finance: "SUMMARY",
        communicationPreview: "FULL",
        memory: "FULL",
        preferences: "FULL",
        privateNotes: "SUMMARY",
        allergyDetails: "FULL",
        auditMetadata: "RESTRICTED",
    }, { metrics: "SUMMARY" }, SALES_ACTIONS.concat("VIEW_OUTSTANDING")),
    SALES: rolePolicy("SALES", {
        phone: "FULL",
        email: "SUMMARY",
        finance: "HIDDEN",
        communicationPreview: "FULL",
        memory: "SUMMARY",
        preferences: "FULL",
        privateNotes: "SUMMARY",
        allergyDetails: "FULL",
        auditMetadata: "HIDDEN",
    }, { metrics: "SUMMARY" }, SALES_ACTIONS),
    PRODUCTION: rolePolicy("PRODUCTION", {
        phone: "HIDDEN",
        email: "HIDDEN",
        finance: "HIDDEN",
        communicationPreview: "HIDDEN",
        memory: "HIDDEN",
        preferences: "TASK_ONLY",
        privateNotes: "HIDDEN",
        allergyDetails: "FULL",
        auditMetadata: "HIDDEN",
    }, {
        relationship: "HIDDEN",
        communication: "HIDDEN",
        metrics: "HIDDEN",
        memory: "HIDDEN",
        dataCompleteness: "HIDDEN",
    }, []),
    DELIVERY: rolePolicy("DELIVERY", {
        phone: "TASK_ONLY",
        email: "HIDDEN",
        finance: "TASK_ONLY",
        communicationPreview: "HIDDEN",
        memory: "HIDDEN",
        preferences: "TASK_ONLY",
        privateNotes: "HIDDEN",
        allergyDetails: "TASK_ONLY",
        auditMetadata: "HIDDEN",
    }, {
        relationship: "HIDDEN",
        communication: "HIDDEN",
        metrics: "HIDDEN",
        memory: "HIDDEN",
        dataCompleteness: "HIDDEN",
    }, ["CALL"]),
    ACCOUNTS: rolePolicy("ACCOUNTS", {
        phone: "SUMMARY",
        email: "SUMMARY",
        finance: "FULL",
        communicationPreview: "HIDDEN",
        memory: "HIDDEN",
        preferences: "HIDDEN",
        privateNotes: "HIDDEN",
        allergyDetails: "HIDDEN",
        auditMetadata: "HIDDEN",
    }, {
        relationship: "SUMMARY",
        communication: "HIDDEN",
        preferences: "HIDDEN",
        memory: "HIDDEN",
        dataCompleteness: "HIDDEN",
    }, ["VIEW_OUTSTANDING"]),
    MARKETING: rolePolicy("MARKETING", {
        phone: "MASKED",
        email: "MASKED",
        finance: "HIDDEN",
        communicationPreview: "HIDDEN",
        memory: "SUMMARY",
        preferences: "SUMMARY",
        privateNotes: "HIDDEN",
        allergyDetails: "HIDDEN",
        auditMetadata: "HIDDEN",
    }, {
        relationship: "SUMMARY",
        communication: "HIDDEN",
        metrics: "SUMMARY",
        dataCompleteness: "HIDDEN",
        quickActions: "RESTRICTED",
    }, ["CREATE_INQUIRY"]),
};
const UNKNOWN_ROLE_POLICY = {
    role: "UNKNOWN",
    sections: Object.fromEntries(CUSTOMER_360_SECTION_IDS.map((section) => [section, "HIDDEN"])),
    fields: {
        phone: "HIDDEN",
        email: "HIDDEN",
        finance: "HIDDEN",
        communicationPreview: "HIDDEN",
        memory: "HIDDEN",
        preferences: "HIDDEN",
        privateNotes: "HIDDEN",
        allergyDetails: "HIDDEN",
        auditMetadata: "HIDDEN",
    },
    editable: Object.fromEntries(CUSTOMER_360_SECTION_IDS.map((section) => [section, "NOT_AVAILABLE"])),
    quickActions: [],
};
const FORBIDDEN_CONTRACT_KEYS = new Set([
    "rawPayload",
    "providerPayload",
    "providerMessageId",
    "waMessageId",
    "messageId",
    "accessToken",
    "password",
    "passwordHash",
    "token",
    "refreshToken",
    "session",
    "secret",
]);
export function getCustomer360VisibilityForRole(role) {
    return CUSTOMER_360_ROLE_VISIBILITY[role] ?? UNKNOWN_ROLE_POLICY;
}
export function maskPhone(phone) {
    if (!phone)
        return null;
    const digits = phone.replace(/\D/g, "");
    if (digits.length <= 4)
        return "****";
    return `${"*".repeat(Math.max(4, digits.length - 4))}${digits.slice(-4)}`;
}
export function maskEmail(email) {
    if (!email)
        return null;
    const [name, domain] = email.split("@");
    if (!name || !domain)
        return "***";
    const visible = name.slice(0, 1);
    return `${visible}${"*".repeat(Math.max(3, name.length - 1))}@${domain}`;
}
export function hasCustomer360Permission(permissions, permission) {
    return permissions.includes(permission);
}
export function filterCustomer360ForRole(overview, role, permissions = []) {
    const policy = getCustomer360VisibilityForRole(role);
    const filtered = stripForbiddenKeys(cloneJson(overview));
    const permissionSet = new Set(permissions);
    applyCustomerIdentityPolicy(filtered, policy);
    applyFinancePolicy(filtered, policy, permissionSet);
    applyOperationalPermissionPolicy(filtered, permissionSet);
    applyCommunicationPolicy(filtered, policy, permissionSet);
    applyMemoryPolicy(filtered, policy, permissionSet);
    applyPreferencePolicy(filtered, policy);
    applyStructuredMemoryPolicy(filtered, policy);
    applyAlertPolicy(filtered, policy);
    applyDataCompletenessPolicy(filtered, policy);
    applyQuickActionPolicy(filtered, policy, permissionSet);
    applyMetadataPolicy(filtered, policy, permissionSet);
    return filtered;
}
function applyCustomerIdentityPolicy(overview, policy) {
    if (policy.fields.phone === "MASKED" || policy.fields.phone === "SUMMARY") {
        overview.customer.maskedPhone = overview.customer.maskedPhone ?? maskPhone(overview.customer.primaryPhone);
        delete overview.customer.primaryPhone;
    }
    if (policy.fields.phone === "HIDDEN") {
        delete overview.customer.primaryPhone;
        delete overview.customer.maskedPhone;
    }
    if (policy.fields.email === "MASKED" || policy.fields.email === "SUMMARY") {
        overview.customer.maskedEmail = overview.customer.maskedEmail ?? maskEmail(overview.customer.email);
        delete overview.customer.email;
    }
    if (policy.fields.email === "HIDDEN") {
        delete overview.customer.email;
        delete overview.customer.maskedEmail;
    }
}
function applyFinancePolicy(overview, policy, permissions) {
    const canSeeFinance = policy.fields.finance === "FULL" && permissions.has(PERMISSIONS.INVOICE_READ);
    const canSeeFinanceSummary = canSeeFinance || (policy.fields.finance === "SUMMARY" && permissions.has(PERMISSIONS.INVOICE_READ));
    if (!canSeeFinance) {
        delete overview.metrics.totalSpendPaise;
        delete overview.metrics.averageOrderValuePaise;
    }
    if (!canSeeFinanceSummary) {
        delete overview.metrics.outstandingPaise;
        overview.operationalState.outstandingAmount = {
            value: null,
            source: "UNAVAILABLE",
            sourceModel: "Payment",
        };
    }
    overview.summaryCards = overview.summaryCards.map((card) => {
        if (card.type !== "OUTSTANDING_AMOUNT")
            return card;
        if (canSeeFinanceSummary)
            return card;
        return {
            ...card,
            state: "RESTRICTED",
            value: null,
            amountPaise: null,
            subtitle: "Restricted by role policy",
            source: "UNAVAILABLE",
        };
    });
}
function applyOperationalPermissionPolicy(overview, permissions) {
    if (!permissions.has(PERMISSIONS.LEAD_READ)) {
        overview.relationship.openInquiryId = null;
        overview.operationalState.openInquiry = { value: null, source: "UNAVAILABLE", sourceModel: "Inquiry" };
        overview.summaryCards = overview.summaryCards.map((card) => {
            if (card.type !== "OPEN_INQUIRY")
                return card;
            return {
                ...card,
                state: "RESTRICTED",
                value: null,
                subtitle: "Restricted by permission policy",
                source: "UNAVAILABLE",
            };
        });
        overview.alerts = overview.alerts.filter((alert) => alert.type !== "NO_FOLLOW_UP");
    }
    if (!permissions.has(PERMISSIONS.FOLLOW_UP_READ)) {
        overview.relationship.pendingFollowUpId = null;
        overview.operationalState.pendingFollowUp = { value: null, source: "UNAVAILABLE", sourceModel: "FollowUpTask" };
        overview.alerts = overview.alerts.filter((alert) => alert.type !== "NO_FOLLOW_UP");
    }
}
function applyCommunicationPolicy(overview, policy, permissions) {
    const canReadWhatsapp = permissions.has(PERMISSIONS.WHATSAPP_READ);
    if (policy.fields.communicationPreview === "FULL" && canReadWhatsapp) {
        overview.communication.canOpenConversation = true;
        return;
    }
    overview.communication = {
        state: policy.fields.communicationPreview === "HIDDEN" ? "RESTRICTED" : overview.communication.state,
        canOpenConversation: false,
        source: policy.fields.communicationPreview === "HIDDEN" || !canReadWhatsapp ? "UNAVAILABLE" : overview.communication.source,
    };
}
function applyMemoryPolicy(overview, policy, permissions) {
    const canReadMemory = permissions.has(PERMISSIONS.INTELLIGENCE_CUSTOMER_READ);
    if (policy.fields.memory === "FULL" && canReadMemory)
        return;
    if (policy.fields.memory === "SUMMARY" && canReadMemory) {
        overview.memory.summaryLines.value = (overview.memory.summaryLines.value ?? []).slice(0, 2);
        delete overview.memory.emotionalTags.sourceId;
        return;
    }
    overview.memory = {
        available: false,
        summaryLines: { value: [], source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        emotionalTags: { value: [], source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        nextPredictedOccasion: { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        nextPredictedDate: { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        churnRiskLabel: { value: "UNKNOWN", source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        repeatProbabilityLabel: { value: "UNKNOWN", source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
    };
}
function applyPreferencePolicy(overview, policy) {
    if (policy.fields.preferences === "HIDDEN") {
        overview.preferences = {
            favoriteFlavors: { value: [], source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
            favoriteOccasions: { value: [], source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
            preferredDeliveryTime: { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
            allergyWarning: { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
            dislikes: { value: [], source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
            dietaryPreference: { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" },
        };
        return;
    }
    if (policy.fields.allergyDetails === "HIDDEN") {
        overview.preferences.allergyWarning = { value: null, source: "UNAVAILABLE", sourceModel: "CustomerProfile" };
    }
    if (policy.fields.preferences === "TASK_ONLY") {
        overview.preferences.favoriteFlavors.value = [];
        overview.preferences.favoriteOccasions.value = [];
        overview.preferences.dislikes.value = [];
    }
}
function applyStructuredMemoryPolicy(overview, policy) {
    if (!overview.structuredMemory)
        return;
    if (policy.fields.preferences === "HIDDEN") {
        overview.structuredMemory.preferences = [];
        overview.structuredMemory.occasions = [];
        overview.structuredMemory.primaryAddress = null;
    }
    if (policy.fields.preferences === "SUMMARY") {
        overview.structuredMemory.preferences = overview.structuredMemory.preferences
            .filter((preference) => !["ALLERGY", "DIETARY"].includes(preference.category))
            .slice(0, 5);
        overview.structuredMemory.occasions = overview.structuredMemory.occasions.slice(0, 3);
        if (overview.structuredMemory.primaryAddress) {
            overview.structuredMemory.primaryAddress = {
                id: overview.structuredMemory.primaryAddress.id,
                label: overview.structuredMemory.primaryAddress.label,
                city: overview.structuredMemory.primaryAddress.city,
                state: overview.structuredMemory.primaryAddress.state,
                isPrimary: overview.structuredMemory.primaryAddress.isPrimary,
                source: overview.structuredMemory.primaryAddress.source,
            };
        }
    }
    if (policy.fields.allergyDetails === "HIDDEN") {
        overview.structuredMemory.preferences = overview.structuredMemory.preferences.filter((preference) => preference.category !== "ALLERGY");
    }
    if (policy.fields.privateNotes === "HIDDEN") {
        overview.structuredMemory.notes = { count: 0, latestAt: null, pinned: null };
    }
}
function applyAlertPolicy(overview, policy) {
    overview.alerts = overview.alerts.filter((alert) => {
        if (policy.fields.finance === "HIDDEN" && alert.type === "PAYMENT_OVERDUE")
            return false;
        if (policy.fields.allergyDetails === "HIDDEN" && alert.type === "ALLERGY_WARNING")
            return false;
        if (policy.fields.preferences === "HIDDEN" && alert.type === "UPCOMING_OCCASION")
            return false;
        if (alert.visibility === "HIDDEN" || alert.visibility === "RESTRICTED")
            return false;
        return true;
    });
}
function applyDataCompletenessPolicy(overview, policy) {
    if (policy.sections.dataCompleteness !== "HIDDEN")
        return;
    overview.dataCompleteness = {
        score: 0,
        requiredFields: [],
        missingFields: [],
        roleSafeMissingFields: [],
        recommendedCapture: [],
        source: "UNAVAILABLE",
    };
}
function applyQuickActionPolicy(overview, policy, permissions) {
    overview.quickActions = overview.quickActions.map((action) => {
        const requiredPermission = CUSTOMER_360_QUICK_ACTION_REQUIRED_PERMISSIONS[action.id];
        const allowedByRole = policy.quickActions.includes(action.id);
        const allowedByPermission = permissions.has(requiredPermission);
        const enabled = action.enabled && allowedByRole && allowedByPermission;
        return {
            ...action,
            enabled,
            requiredPermission,
            route: enabled ? action.route : undefined,
            target: enabled ? action.target : undefined,
            disabledReason: enabled
                ? undefined
                : action.disabledReason ?? (!allowedByRole ? "Restricted by role policy" : "Missing required permission"),
            explicitHumanActionRequired: true,
        };
    });
}
function applyMetadataPolicy(overview, policy, permissions) {
    if (policy.role !== "UNKNOWN") {
        overview.metadata.role = policy.role;
    }
    const sectionsRestricted = CUSTOMER_360_SECTION_IDS.filter((section) => policy.sections[section] !== "FULL");
    overview.metadata.sectionsRestricted = sectionsRestricted;
    overview.metadata.sectionsIncluded = CUSTOMER_360_SECTION_IDS.filter((section) => policy.sections[section] !== "HIDDEN");
    overview.metadata.dataSources = overview.metadata.dataSources.filter((source) => source !== "UNAVAILABLE");
    overview.metadata.audit = {
        visible: policy.fields.auditMetadata === "FULL" && permissions.has(PERMISSIONS.ADMIN_AUDIT_READ),
        source: policy.fields.auditMetadata === "FULL" ? "STAFF_ENTERED" : "UNAVAILABLE",
    };
}
function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
}
function stripForbiddenKeys(value) {
    if (Array.isArray(value))
        return value.map(stripForbiddenKeys);
    if (!value || typeof value !== "object")
        return value;
    const output = {};
    for (const [key, entry] of Object.entries(value)) {
        if (FORBIDDEN_CONTRACT_KEYS.has(key))
            continue;
        output[key] = stripForbiddenKeys(entry);
    }
    return output;
}
